import React, { useState, useCallback, useContext } from "react";
import { PurchasingCalendarEditsContext } from "./context";
import { DeliveryDateEdits, EditEntry } from "./type";
import { useApi, useCustomer, useWasm } from "../app/provider";
import {
  useSavePurchasingCalendarOverrideToDefaultSettingsMutation,
  UserRunSettingsInput,
  GetMyDataFileDownloadUrlDocument,
  GetMyPurchasingCalendarOverrideUploadUrlDocument,
} from "../../services/api/__generated__/backend_gateway-types";
import { VersionedDataContext } from "../versioned_data/context";
import { SysMessagesContext } from "../sys_messages/context";
import { SysMessageLevel } from "../sys_messages/type";
import { useLocation } from "react-router-dom";

import {
  Table as WasmTable,
  WriterPropertiesBuilder,
  Compression,
  writeParquet,
  readParquet,
} from "parquet-wasm";

import * as arrow from "apache-arrow";
import {
  makeBuilder,
  Utf8,
  Timestamp,
  TimeUnit,
  tableToIPC,
  tableFromIPC,
  Table,
} from "apache-arrow";

const MS_PER_DAY = 86400000;

interface PurchasingCalendarEditsProviderProps {
  children: React.ReactNode;
}

/** Effective state for one SKU row in the override map. */
interface SkuOverrideState {
  planning_cycle: string;
  sku: string;
  supplier: string;
  location: string;
  order_date: number; // epoch ms
  delivery_date: number; // epoch ms
  is_deleted: boolean;
  is_edited: boolean;
}

export const PurchasingCalendarEditsProvider: React.FC<
  PurchasingCalendarEditsProviderProps
> = ({ children }) => {
  const { apiService } = useApi();
  const { wasmInitialized, wasmError } = useWasm();
  const { whoIAm } = useCustomer();
  const {
    mainRun,
    lastSuccessfulRun,
    engineInProgress,
    justTriggeredRun,
    setJustTriggeredRun,
    loading: runsLoading,
  } = useContext(VersionedDataContext);
  const { addSystemMessage } = useContext(SysMessagesContext);
  const { search } = useLocation();

  const [deliveryDateEdits, setDeliveryDateEdits] =
    useState<DeliveryDateEdits>({});

  const [editMode, setEditModeState] = useState<boolean>(false);

  const [savePurchasingCalendarOverride, { loading }] =
    useSavePurchasingCalendarOverrideToDefaultSettingsMutation({
      client: apiService.getClient(),
    });

  const setEditMode = useCallback((mode: boolean) => {
    setEditModeState(mode);
  }, []);

  const clearEdits = useCallback(() => {
    setDeliveryDateEdits({});
  }, []);

  const discardChanges = useCallback(() => {
    clearEdits();
    setEditModeState(false);
  }, [clearEdits]);

  const saveEditsAndTriggerRun = useCallback(
    async (runName: string) => {
      try {
        if (loading || runsLoading || engineInProgress || justTriggeredRun !== null) {
          addSystemMessage({
            level: SysMessageLevel.WARNING,
            message: "A run is already in progress. Please wait for it to complete.",
          });
          return;
        }

        const runToUse = mainRun || lastSuccessfulRun;

        if (!runToUse) {
          console.error("No run available for upload and trigger");
          return;
        }

        if (!wasmInitialized) {
          throw new Error("WASM not initialized yet.");
        }
        if (wasmError) {
          throw new Error(`WASM initialization failed: ${wasmError}`);
        }

        // Optimistically mark a run as triggered so the START RUN button
        // greys out immediately on click. The save flow now does 3 sequential
        // network calls (presigned URL → S3 PUT → save mutation) which can
        // total 1-3s; without this the button stays clickable for the whole
        // duration. If any step fails, the catch block clears the state.
        setJustTriggeredRun({
          runSettings: {
            ...runToUse.runSettings,
            organizationId: "",
          },
          status: "RUN_IN_PROGRESS",
          createdAt: new Date().toISOString(),
          stepIds: "[]",
          updatedAt: new Date().toISOString(),
          initiatedBy: whoIAm?.email || null,
          name: null,
          description: null,
          displayId: null,
          atStep: 0,
          totalSteps: 1,
          id: "",
        });

        // Get segmentation fields from URL (for matching local edits)
        const urlParams = new URLSearchParams(search);
        const segmentsParam = urlParams.get("segments");
        let segments: string[] = [];
        if (segmentsParam) {
          try {
            segments = JSON.parse(segmentsParam);
          } catch {
            // Fall back to empty
          }
        }

        // ── Step 1: Download raw purchasing_calendar.parquet ──
        // This has per-SKU rows with is_edited / is_deleted flags from the
        // engine's most recent run. We use it as the source of truth for
        // which SKUs have overrides and to fan out local edits to SKU level.
        const rawCalendarRows = await downloadRawCalendar(
          apiService.getClient(),
          runToUse.runSettings
        );

        // ── Step 2: Build override map from prior edits ──
        // Key = "planning_cycle|sku|supplier|location"
        const overrideMap = new Map<string, SkuOverrideState>();

        for (const row of rawCalendarRows) {
          if (!row.is_edited && !row.is_deleted) continue;
          const key = skuKey(row);
          overrideMap.set(key, {
            planning_cycle: row.planning_cycle,
            sku: row.sku,
            supplier: row.supplier,
            location: row.location,
            order_date: row.order_date,
            delivery_date: row.delivery_date,
            is_deleted: row.is_deleted,
            is_edited: row.is_edited,
          });
        }

        // ── Step 3: Apply local edits on top ──
        // Local edits are keyed at the user's segmentation level. Fan out
        // each to all matching SKUs in the raw calendar.
        for (const [editKey, editEntry] of Object.entries(deliveryDateEdits)) {
          if (!editEntry) continue;

          const parts = editKey.split("|");
          const planningCycle = parts[0];
          const segValues: Record<string, string> = {};
          segments.forEach((seg, i) => {
            segValues[seg] = parts[i + 1] ?? "";
          });

          // Find all raw calendar SKUs matching this edit key
          const matchingRows = rawCalendarRows.filter((row) => {
            if (row.planning_cycle !== planningCycle) return false;
            return segments.every(
              (seg) => String(row[seg as keyof RawCalendarRow] ?? "") === segValues[seg]
            );
          });

          for (const matchRow of matchingRows) {
            const key = skuKey(matchRow);
            applyLocalEdit(overrideMap, key, matchRow, editEntry);
          }
        }

        // ── Step 4: Check we have something to save ──
        // An empty override map is a valid save WHEN the user made local edits
        // this session: restoring the last remaining deleted/edited cycle clears
        // the override entirely, and we must persist that empty state so the
        // engine reverts to defaults. Only bail when there were no local edits
        // at all (nothing changed).
        const hasLocalEdits = Object.keys(deliveryDateEdits).length > 0;
        if (overrideMap.size === 0 && !hasLocalEdits) {
          addSystemMessage({
            level: SysMessageLevel.WARNING,
            message:
              "No valid edits found to save. Please make some changes first.",
          });
          return;
        }

        // ── Step 5: Write state-based override parquet ──
        // One row per SKU with the complete final state (order_date,
        // delivery_date, is_deleted). No edit_type/edit_value — just
        // "this is what the values should be."
        const tsUTCms = new Timestamp(TimeUnit.MILLISECOND, "UTC");

        const planningCycleB = makeBuilder({ type: new Utf8() });
        const skuB = makeBuilder({ type: new Utf8() });
        const supplierB = makeBuilder({ type: new Utf8() });
        const locationB = makeBuilder({ type: new Utf8() });
        const orderDateB = makeBuilder({ type: tsUTCms });
        const deliveryDateB = makeBuilder({ type: tsUTCms });
        const isDeletedB = makeBuilder({ type: new arrow.Bool() });
        const editedByB = makeBuilder({ type: new Utf8() });
        const editedAtB = makeBuilder({ type: tsUTCms });

        const userEmail = whoIAm?.email || "unknown";
        const now = Date.now();

        for (const state of overrideMap.values()) {
          planningCycleB.append(state.planning_cycle);
          skuB.append(state.sku);
          supplierB.append(state.supplier);
          locationB.append(state.location);
          orderDateB.append(state.order_date);
          deliveryDateB.append(state.delivery_date);
          isDeletedB.append(state.is_deleted);
          editedByB.append(userEmail);
          editedAtB.append(now);
        }

        const vectors: Record<string, any> = {
          planning_cycle: planningCycleB.finish().toVector(),
          sku: skuB.finish().toVector(),
          supplier: supplierB.finish().toVector(),
          location: locationB.finish().toVector(),
          order_date: orderDateB.finish().toVector(),
          delivery_date: deliveryDateB.finish().toVector(),
          is_deleted: isDeletedB.finish().toVector(),
          edited_by: editedByB.finish().toVector(),
          edited_at: editedAtB.finish().toVector(),
        };

        const table = new Table(vectors);

        if (table.numRows === 0 && !hasLocalEdits) {
          addSystemMessage({
            level: SysMessageLevel.WARNING,
            message: "No valid edits found to save.",
          });
          return;
        }

        const wasmTable = WasmTable.fromIPCStream(tableToIPC(table, "stream"));
        const props = new WriterPropertiesBuilder()
          .setCompression(Compression.SNAPPY)
          .build();
        const parquetBytes: Uint8Array = writeParquet(wasmTable, props);

        // ── Step 6: Upload via presigned URL (bypasses API Gateway / WAF) ──
        // Goodchop's calendar override parquet runs >8 KB after base64 +
        // GraphQL envelope, which trips WAF's SizeRestrictions_Body rule on
        // the mutation body. Routing the upload through S3 directly keeps the
        // subsequent save mutation body tiny (just the s3Key string).
        const apolloClient = apiService.getClient();
        const urlResult = await apolloClient.query({
          query: GetMyPurchasingCalendarOverrideUploadUrlDocument,
          fetchPolicy: "no-cache",
        });
        const credentials =
          urlResult.data?.getMyPurchasingCalendarOverrideUploadUrl;
        if (!credentials) {
          throw new Error(
            "Could not get presigned upload URL for purchasing calendar override"
          );
        }
        const { uploadUrl, s3Key } = credentials;
        const putResponse = await fetch(uploadUrl, {
          method: "PUT",
          body: parquetBytes,
        });
        if (!putResponse.ok) {
          throw new Error(
            `Failed to upload override parquet to S3: ${putResponse.status} ${putResponse.statusText}`
          );
        }

        const [settingsName] =
          runToUse.runSettings.settingsNameAndVersion.split("|");

        const runSettings: UserRunSettingsInput = {
          inputId: runToUse.runSettings.inputId,
          workflowId: runToUse.runSettings.workflowId,
          settingsName,
          runAttemptId: runToUse.runSettings.runAttemptId,
        };

        await savePurchasingCalendarOverride({
          variables: { s3Key, runSettings, runName },
        });

        // justTriggeredRun was already set optimistically above when the save
        // flow began, so the UI is already showing "a run is in progress".
        setEditMode(false);
      } catch (error: unknown) {
        // Roll back the optimistic justTriggeredRun so the user can retry.
        setJustTriggeredRun(null);
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: "Failed to save purchasing calendar override.",
        });
        console.error("Failed to save purchasing calendar override:", error);
      }
    },
    [
      savePurchasingCalendarOverride,
      mainRun,
      lastSuccessfulRun,
      deliveryDateEdits,
      addSystemMessage,
      search,
      wasmInitialized,
      wasmError,
      whoIAm,
      setEditMode,
      apiService,
      engineInProgress,
      justTriggeredRun,
      setJustTriggeredRun,
      loading,
      runsLoading,
    ]
  );

  const value = {
    deliveryDateEdits,
    editMode,
    loading,
    setDeliveryDateEdits,
    setEditMode,
    saveEditsAndTriggerRun,
    discardChanges,
  };

  return (
    <PurchasingCalendarEditsContext.Provider value={value}>
      {children}
    </PurchasingCalendarEditsContext.Provider>
  );
};

// ─── Raw calendar download ─────────────────────────────────────────────

interface RawCalendarRow {
  planning_cycle: string;
  sku: string;
  supplier: string;
  location: string;
  order_date: number; // epoch ms
  delivery_date: number; // epoch ms
  is_edited: boolean;
  is_deleted: boolean;
  [key: string]: unknown; // allow dynamic seg column access
}

/**
 * Download the raw `purchasing_calendar.parquet` for the given run, parse it
 * with parquet-wasm, and return the rows as a flat array.
 */
async function downloadRawCalendar(
  client: any,
  runSettings: {
    inputId: string;
    workflowId: string;
    settingsNameAndVersion: string;
    runAttemptId: string;
  }
): Promise<RawCalendarRow[]> {
  // Get a signed download URL via GraphQL
  const result = await client.query({
    query: GetMyDataFileDownloadUrlDocument,
    variables: {
      runSettings: {
        inputId: runSettings.inputId,
        workflowId: runSettings.workflowId,
        settingsNameAndVersion: runSettings.settingsNameAndVersion,
        runAttemptId: runSettings.runAttemptId,
      },
      dataName: "purchasing_calendar",
      fileFormat: "parquet",
    },
    fetchPolicy: "no-cache",
  });

  const downloadUrl = result.data?.getMyDataFileDownloadUrl;
  if (!downloadUrl) {
    throw new Error("Could not get download URL for purchasing_calendar.parquet");
  }

  // Download and parse with parquet-wasm
  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error(`Failed to download purchasing_calendar: ${response.status}`);
  }

  const bytes = new Uint8Array(await response.arrayBuffer());
  const wasmTable = readParquet(bytes);
  const arrowTable = tableFromIPC(wasmTable.intoIPCStream());

  // Extract rows
  const rows: RawCalendarRow[] = [];
  const colNames = arrowTable.schema.fields.map((f) => f.name);

  for (let i = 0; i < arrowTable.numRows; i++) {
    const row: Record<string, unknown> = {};
    for (const col of colNames) {
      row[col] = arrowTable.getChild(col)?.get(i);
    }
    rows.push({
      planning_cycle: String(row.planning_cycle ?? ""),
      sku: String(row.sku ?? ""),
      supplier: String(row.supplier ?? ""),
      location: String(row.location ?? ""),
      order_date: toMs(row.order_date),
      delivery_date: toMs(row.delivery_date),
      is_edited: Boolean(row.is_edited),
      is_deleted: Boolean(row.is_deleted),
      ...row,
    });
  }

  console.log(
    `[purchasing_calendar save] Downloaded raw calendar: ${rows.length} rows, ` +
    `${rows.filter((r) => r.is_edited).length} edited, ` +
    `${rows.filter((r) => r.is_deleted).length} deleted`
  );

  return rows;
}

// ─── Helpers ────────────────────────────────────────────────────────────

function skuKey(row: { planning_cycle: string; sku: string; supplier: string; location: string }): string {
  return `${row.planning_cycle}|${row.sku}|${row.supplier}|${row.location}`;
}

/**
 * Apply a local edit to the override map for one specific SKU row.
 * Local edits always overwrite prior state at the same key.
 */
function applyLocalEdit(
  map: Map<string, SkuOverrideState>,
  key: string,
  rawRow: RawCalendarRow,
  edit: EditEntry
): void {
  const base: SkuOverrideState = map.get(key) ?? {
    planning_cycle: rawRow.planning_cycle,
    sku: rawRow.sku,
    supplier: rawRow.supplier,
    location: rawRow.location,
    order_date: rawRow.order_date,
    delivery_date: rawRow.delivery_date,
    is_deleted: rawRow.is_deleted,
    is_edited: rawRow.is_edited,
  };

  switch (edit.editType) {
    case "delete":
      map.set(key, { ...base, is_deleted: true, is_edited: false });
      break;
    case "restore":
      // Restore removes ALL overrides for this SKU — revert to engine default
      map.delete(key);
      break;
    case "delivery_date":
      map.set(key, {
        ...base,
        delivery_date: edit.editValue,
        is_deleted: false,
        is_edited: true,
      });
      break;
    case "order_date": {
      // Keep the per-SKU lead time (delta between delivery and order)
      const leadMs = base.delivery_date - base.order_date;
      map.set(key, {
        ...base,
        order_date: edit.editValue,
        delivery_date: edit.editValue + leadMs,
        is_deleted: false,
        is_edited: true,
      });
      break;
    }
    case "calculated_lead_time": {
      const effectiveOrderDate = edit.orderDateMs ?? base.order_date;
      map.set(key, {
        ...base,
        order_date: effectiveOrderDate,
        delivery_date: effectiveOrderDate + edit.editValue * MS_PER_DAY,
        is_deleted: false,
        is_edited: true,
      });
      break;
    }
    case "days_change": {
      // days_change is relative to the DEFAULT lead time, not the current delivery_date.
      // Use edited order_date if the user also changed it in this session.
      const defaultLt = toNumber(rawRow["default_lead_time_bucket_days"]);
      const effectiveOrderDate = edit.orderDateMs ?? base.order_date;
      map.set(key, {
        ...base,
        order_date: effectiveOrderDate,
        delivery_date: effectiveOrderDate + (defaultLt + edit.editValue) * MS_PER_DAY,
        is_deleted: false,
        is_edited: true,
      });
      break;
    }
  }
}

function toNumber(value: unknown): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === "bigint") return Number(value);
  if (typeof value === "number") return value;
  const n = Number(value);
  return isNaN(n) ? 0 : n;
}

function toMs(value: unknown): number {
  if (value === null || value === undefined) return NaN;
  if (typeof value === "bigint") return Number(value / BigInt(1000));
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  return new Date(value as string).getTime();
}
